export interface BillingSendRecipients {
  primaryEmail: string;
  primaryName: string;
  accountingEmail: string;
  accountingName: string;
  secondaryEmail: string;
  secondaryName: string;
  ccEmail: string;
  ccName: string;
  hasPrimaryContact: boolean;
  hasAccountingContact: boolean;
  primaryAndAccountingSamePerson: boolean;
  canComparePrimaryAndAccounting: boolean;
  usesPrimaryOfficeContacts?: boolean;
  inheritNotice?: string | null;
  primaryEmailInherited?: boolean;
  accountingEmailInherited?: boolean;
  primaryJurisdictionName?: string;
}

export const EMPTY_BILLING_SEND_RECIPIENTS: BillingSendRecipients = {
  primaryEmail: "",
  primaryName: "",
  accountingEmail: "",
  accountingName: "",
  secondaryEmail: "",
  secondaryName: "",
  ccEmail: "",
  ccName: "",
  hasPrimaryContact: false,
  hasAccountingContact: false,
  primaryAndAccountingSamePerson: false,
  canComparePrimaryAndAccounting: false,
  usesPrimaryOfficeContacts: false,
  inheritNotice: null,
};

export interface BillingEmailAddressing {
  toEmail: string;
  toName: string;
  ccEmail: string;
  ccName: string;
  /** "cc" is the CC contact. "secondary" is used only when there is no accounting contact and no CC contact. */
  ccRole: "cc" | "secondary" | "none";
}

function sameEmail(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/**
 * To is the accounting contact when one exists, otherwise the primary contact.
 * CC is the CC contact when one exists. If there is no accounting contact and
 * no CC contact, the secondary contact is copied instead.
 */
export function resolveBillingEmailAddressing(
  recipients: BillingSendRecipients,
): BillingEmailAddressing {
  const accountingEmail = recipients.accountingEmail.trim();
  const primaryEmail = recipients.primaryEmail.trim();
  const ccContactEmail = recipients.ccEmail.trim();
  const secondaryEmail = recipients.secondaryEmail.trim();

  const toEmail = accountingEmail || primaryEmail;
  const toName = accountingEmail
    ? recipients.accountingName
    : recipients.primaryName;

  if (ccContactEmail && !sameEmail(ccContactEmail, toEmail)) {
    return {
      toEmail,
      toName,
      ccEmail: ccContactEmail,
      ccName: recipients.ccName,
      ccRole: "cc",
    };
  }

  if (!accountingEmail && secondaryEmail && !sameEmail(secondaryEmail, toEmail)) {
    return {
      toEmail,
      toName,
      ccEmail: secondaryEmail,
      ccName: recipients.secondaryName,
      ccRole: "secondary",
    };
  }

  return {
    toEmail,
    toName,
    ccEmail: "",
    ccName: "",
    ccRole: "none",
  };
}

/** Human-readable reason Send is disabled; null when billing can be sent. */
export function getSendBillingDisabledReason(
  recipients: BillingSendRecipients,
): string | null {
  if (resolveBillingEmailAddressing(recipients).toEmail) {
    return null;
  }
  if (!recipients.hasAccountingContact && !recipients.hasPrimaryContact) {
    return "No accounting or primary contact assigned.";
  }
  if (recipients.hasAccountingContact && !recipients.accountingEmail.trim()) {
    return "Accounting contact has no email address.";
  }
  if (!recipients.hasPrimaryContact) {
    return "No primary contact assigned.";
  }
  return "Primary contact has no email address.";
}
